/**
 * Central redaction policy. We keep maximum debugging signal (user_id,
 * user_email, request/response bodies, geo) and strip ONLY true secrets that
 * could be replayed if a log reader got hold of them.
 *
 * Two surfaces:
 *  - body/attribute keys  → isSensitiveKey()   (substring match)
 *  - HTTP header names     → isSensitiveHeader() (substring + a Vercel exact set)
 */

// Body/attribute key fragments. Mirrors the historical list in log-attributes.ts
// so existing behaviour is preserved; this is now the single source of truth.
import { redactUrlCredentials } from "./redact-url-credentials"

const SENSITIVE_KEY_PATTERNS = [
  "password",
  "passwd",
  "token",
  "authorization",
  "auth_header",
  "secret",
  "api_key",
  "apikey",
  "cookie",
  "session_token",
  "jwt",
  "access_token",
  "refresh_token",
  "credit_card",
  "card_number",
  "cvv",
  "ssn",
  "private_key",
  "client_secret",
  "signature", // payment-gateway webhook HMAC etc.
  "key_value", // speechmatics temp key field
  "handoff_code",
  "handoffcode",
  "desktop_nonce",
  "desktopnonce",
  "code_verifier",
  "authorization_code",
  "oauth_code",
  "oauth_state",
  "uploadurl",
  "upload_url",
  "signedurl",
  "signed_url",
  "presigned",
]

export function isSensitiveKey(name: string): boolean {
  const lower = name.toLowerCase()
  return SENSITIVE_KEY_PATTERNS.some((pat) => lower.includes(pat))
}

// Personal data carried in request/response BODIES. Distinct from the secrets
// list above: a secret is dropped outright because it could be replayed, whereas
// personal data keeps its `_length` sibling so the field's presence and size
// remain debuggable while the content itself never reaches the log pipeline.
//
// This exists because withRequestLogging flattens every JSON body into log
// attributes across ~100 routes. That is deliberate (see the header above), but
// it means resume text, interview transcripts and applicant records were being
// persisted to a third-party log platform in full.
//
// Patterns are matched as substrings, so they are written narrowly on purpose:
// bare "name" would swallow plan_name/event_name/file_name, and bare "address"
// would swallow ip_address, destroying signal we rely on.
const PERSONAL_DATA_KEY_PATTERNS = [
  "resume_text",
  "resume_summary",
  "resume_content",
  "resume_name",
  "extracted_text",
  "transcript",
  "cover_letter",
  "full_name",
  "first_name",
  "last_name",
  "display_name",
  "candidate_name",
  "applicant_name",
  "phone",
  "date_of_birth",
  "street_address",
  "postal_code",
]

export function isPersonalDataKey(name: string): boolean {
  const lower = name.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase()
  return PERSONAL_DATA_KEY_PATTERNS.some((pat) => lower.includes(pat))
}

// Value-level redaction. Some fields carry a secret in the VALUE even when the
// KEY name looks innocent — e.g. `websocketUrl = wss://...?jwt=<key>`, a raw
// Authorization value, or an embedded JWT/API key. Key-name redaction alone
// misses these, so scrub well-known secret shapes from any logged string.
const VALUE_REDACTORS: Array<[RegExp, string]> = [
  // Credentials embedded in a URL/query string — keep the param name, drop value.
  [
    /((?:[?&])(?:jwt|token|access_token|refresh_token|api[-_]?key|apikey|key|key_value|sig|signature|secret|password)=)[^&\s"']+/gi,
    "$1[redacted]",
  ],
  // JSON Web Tokens (header.payload.signature).
  [/eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{6,}/g, "[redacted-jwt]"],
  // Authorization: Bearer <token>.
  [/\b(Bearer)\s+[A-Za-z0-9._~+/-]{8,}=*/gi, "$1 [redacted]"],
  // OpenAI / Anthropic / payment-gateway style secret keys (rzp_ kept for
  // historical Razorpay values; whsec_ covers Standard Webhooks secrets).
  [/\b(?:sk-ant-|sk-|rzp_live_|rzp_test_|whsec_)[A-Za-z0-9_-]{16,}/gi, "[redacted-key]"],
  // AWS access key IDs (used for SES SMTP / S3 in this stack).
  [/\b(?:AKIA|ASIA)[A-Z0-9]{16}\b/g, "[redacted-key]"],
  // Supabase publishable / secret keys (non-JWT sb_… format).
  [/\bsb_(?:secret|publishable)_[A-Za-z0-9_-]{10,}/gi, "[redacted-key]"],
  // (No Stripe patterns — this app does not use Stripe.)
]

export function redactSecretsInString(value: string): string {
  if (!value) return value
  let out = redactUrlCredentials(value)
  for (const [re, repl] of VALUE_REDACTORS) out = out.replace(re, repl)
  return out
}

const MAX_REDACT_DEPTH = 6

// Recursively redact an arbitrary value for safe logging: drop sensitive KEYS
// at any depth and scrub secret-shaped VALUES from every string. Used by the
// (unauthenticated) frontend log proxy so nested attribute objects can't smuggle
// secrets/PII into Datadog, and anywhere a whole object is logged.
export function redactDeep(value: unknown, depth = 0): unknown {
  if (depth > MAX_REDACT_DEPTH) return "[redacted-depth]"
  if (typeof value === "string") return redactSecretsInString(value)
  if (Array.isArray(value)) return value.map((v) => redactDeep(v, depth + 1))
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (isSensitiveKey(k)) continue
      out[k] = redactDeep(v, depth + 1)
    }
    return out
  }
  return value
}

// Like redactDeep but KEEPS every key — only scrubs secret-shaped string VALUES.
// For TRUSTED server-side caller data where dropping keys would lose useful
// signal (e.g. has_token / token_present boolean flags); we only neutralize the
// values, never the keys.
export function redactValuesDeep(value: unknown, depth = 0): unknown {
  if (depth > MAX_REDACT_DEPTH) return "[redacted-depth]"
  if (typeof value === "string") return redactSecretsInString(value)
  if (Array.isArray(value)) return value.map((v) => redactValuesDeep(v, depth + 1))
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      // Desktop login nonces are opaque (no recognizable value signature).
      // Preserve diagnostic keys/boolean flags but never their replayable value.
      out[k] = /desktop_?nonce/i.test(k) && !(typeof v === "boolean" && /^(?:has_|.*_present$)/i.test(k))
        ? "[redacted]" : redactValuesDeep(v, depth + 1)
    }
    return out
  }
  return value
}

// HTTP header name fragments. Header names containing these are credentials.
// "token" covers x-vercel-oidc-token / x-auth-token / x-amz-security-token;
// "signature" covers x-vercel-proxy-signature[-ts]; "cookie" covers set-cookie.
const SENSITIVE_HEADER_PATTERNS = [
  "authorization",
  "cookie",
  "token",
  "secret",
  "signature",
  "api-key",
  "apikey",
  "password",
]

// Headers whose NAME carries no obvious secret fragment but whose VALUE embeds
// credentials.
//  - x-vercel-sc-headers: contains a serialized Authorization bearer.
//  - forwarded (RFC 7239): Vercel populates its `sig=` parameter with a
//    base64-encoded "Bearer <token>" signed-fetch credential (same secret class
//    as the redacted x-vercel-proxy-signature). Redacting the whole header loses
//    no signal — its for=/host=/proto= parts are duplicated in the kept
//    x-forwarded-for / x-forwarded-host / x-forwarded-proto headers + request_ip.
//  - x-interview-session: the HMAC-signed interview token that lets /api/chat
//    skip text-credit deduction. It is a 2-segment token, so neither the header
//    name patterns nor the 3-segment JWT value pattern matched it, and it was
//    being logged in full on every /api/chat request.
const SENSITIVE_HEADER_EXACT = new Set(["x-vercel-sc-headers", "forwarded", "x-interview-session"])

export function isSensitiveHeader(name: string): boolean {
  const lower = name.toLowerCase()
  if (SENSITIVE_HEADER_EXACT.has(lower)) return true
  return SENSITIVE_HEADER_PATTERNS.some((pat) => lower.includes(pat))
}
